import React from 'react';
import { motion } from 'framer-motion';
import {MapPin, Train, DollarSign, Clock, Cherry} from 'lucide-react';
import { useScrollAnimation, useParallax } from '../../hooks/useScrollAnimation';

export default function Kyoto() {
  const scrollY = useParallax();
  const { ref: infoRef, isVisible: infoVisible } = useScrollAnimation(0.2);
  const { ref: districtsRef, isVisible: districtsVisible } = useScrollAnimation(0.2);
  const { ref: sitesRef, isVisible: sitesVisible } = useScrollAnimation(0.2);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Hero with Parallax */}
      <div className="relative h-[500px] overflow-hidden">
        <motion.img
          src="https://images.pexels.com/photos/402028/pexels-photo-402028.jpeg?auto=compress&cs=tinysrgb&w=1600"
          alt="Kyoto"
          className="w-full h-full object-cover"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A2540]/90 via-[#0A2540]/70 to-transparent" />
        <motion.div 
          className="absolute inset-0 flex items-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.h1 
              className="text-6xl font-bold text-white mb-3"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Kyoto
            </motion.h1>
            <motion.p 
              className="text-2xl text-white/90"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Ancient Capital – Temples, Gardens & Tradition
            </motion.p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Info Cards */}
        <motion.div 
          ref={infoRef}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12"
          initial={{ opacity: 0, y: 30 }}
          animate={infoVisible ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {[
            { icon: Clock, color: 'text-[#4A90E2]', label: 'Recommended Stay', value: '3-4 days' },
            { icon: DollarSign, color: 'text-[#F7B32B]', label: 'Daily Budget', value: '¥7,000-13,000' },
            { icon: Train, color: 'text-[#FF6B35]', label: 'Best Transport', value: 'Buses & Bicycles' },
            { icon: Cherry, color: 'text-[#0A2540]', label: 'Peak Season', value: 'Spring & Fall' }
          ].map((item, idx) => (
            <motion.div 
              key={idx}
              className="bg-white rounded-lg p-4 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              initial={{ opacity: 0, y: 20 }}
              animate={infoVisible ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <item.icon className={`w-6 h-6 ${item.color} mb-2`} />
              <p className="text-sm text-gray-500">{item.label}</p>
              <p className="font-semibold text-gray-800">{item.value}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          <motion.div 
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <h2 className="text-3xl font-bold text-[#0A2540] mb-4">About Kyoto</h2>
            <p className="text-gray-600 mb-4">
              As Japan's imperial capital for over 1,000 years, Kyoto preserves the essence of traditional Japanese culture. With 2,000 temples, serene Zen gardens, geisha districts, and seasonal beauty, it's a living museum of Japanese heritage.
            </p>
            <p className="text-gray-600 mb-8">
              Walk through bamboo groves in Arashiyama, marvel at the Golden Pavilion, explore philosopher's path during cherry blossom season, and witness ancient tea ceremonies.
            </p>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">Must-Visit Districts</h3>
            <motion.div 
              ref={districtsRef}
              className="space-y-4 mb-8"
            >
              {[
                { name: 'Gion', desc: 'Historic geisha district, traditional architecture' },
                { name: 'Arashiyama', desc: 'Bamboo grove, temples, scenic river views' },
                { name: 'Higashiyama', desc: 'Temple walk, traditional streets, Kiyomizu-dera' },
                { name: 'Central Kyoto', desc: 'Nijo Castle, Imperial Palace, shopping' }
              ].map((district, idx) => (
                <motion.div 
                  key={idx} 
                  className="bg-white rounded-lg p-4 shadow-sm border-l-4 border-[#FF6B35] hover:shadow-md transition-all duration-300"
                  initial={{ opacity: 0, x: -30 }}
                  animate={districtsVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ x: 10 }}
                >
                  <h4 className="font-semibold text-gray-800 mb-1">{district.name}</h4>
                  <p className="text-sm text-gray-600">{district.desc}</p>
                </motion.div>
              ))}
            </motion.div>

            <h3 className="text-2xl font-bold text-[#0A2540] mb-4">UNESCO World Heritage Sites</h3>
            <motion.ul 
              ref={sitesRef}
              className="space-y-2 text-gray-700"
            >
              {[
                { name: 'Kinkaku-ji', desc: 'Golden Pavilion reflecting in pond' },
                { name: 'Fushimi Inari', desc: '10,000 vermillion torii gates' },
                { name: 'Kiyomizu-dera', desc: 'Wooden stage with panoramic city views' },
                { name: 'Ryoan-ji', desc: 'Famous zen rock garden' },
                { name: 'Nijo Castle', desc: 'Shogun\'s palace with nightingale floors' }
              ].map((site, idx) => (
                <motion.li 
                  key={idx}
                  className="flex items-start hover:bg-white hover:shadow-sm p-2 rounded transition-all duration-200"
                  initial={{ opacity: 0, x: -20 }}
                  animate={sitesVisible ? { opacity: 1, x: 0 } : {}}
                  transition={{ duration: 0.4, delay: idx * 0.08 }}
                >
                  <MapPin className="w-5 h-5 text-[#4A90E2] mr-2 mt-0.5 flex-shrink-0" />
                  <span><strong>{site.name}:</strong> {site.desc}</span>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="bg-gradient-to-br from-[#4A90E2] to-[#0A2540] rounded-xl p-6 text-white shadow-lg sticky top-4">
              <h3 className="text-2xl font-bold mb-4">Cultural Tips</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start">
                  <span className="mr-2">🎫</span>
                  <span>Buy Kyoto bus day pass for unlimited rides (¥700)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">⛩️</span>
                  <span>Visit temples early morning for peaceful experience</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🌸</span>
                  <span>Book 6+ months ahead for cherry blossom season</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">🍵</span>
                  <span>Try a traditional tea ceremony experience</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">👘</span>
                  <span>Rent a kimono and stroll through Gion district</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}